import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { CloseIcon, FolderIcon } from './Icons';
import { uploadImageToCdn } from '../lib/uploadImage';
import '../styles/Nexaprom.css';

interface Promotion {
  id: string;
  title: string;
  description: string;
  category: string;
  collegeName?: string;
  image?: string;
  createdBy: string;
  status: 'pending' | 'approved' | 'rejected';
  tags?: string[];
  createdAt?: string;
}

interface AdditionalField {
  id: number;
  label: string;
  value: string;
}

const Nexaprom: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    collegeName: '',
    image: ''
  });
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [toastState, setToastState] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const API_URL = BACKEND_URL;

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    setToastState({ type, message });
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = setTimeout(() => {
      setToastState(null);
    }, 3500);
  };

  // Fetch approved promotions
  useEffect(() => {
    fetchPromotions();
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('token');
      const headers: Record<string, string> = {};
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      console.log('Fetching approved promotions from:', `${API_URL}/api/promotions/approved`);
      const response = await axios.get(`${API_URL}/api/promotions/approved`, {
        headers
      });
      console.log('Promotions fetched successfully:', response.data);
      console.log('Number of promotions:', response.data?.length || 0);
      setPromotions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
      if (axios.isAxiosError(error)) {
        console.error('Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
      }
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const uploadedUrl = await uploadImageToCdn(file, token, 'promotions');
        setImagePreview(uploadedUrl);
        setFormData({ ...formData, image: uploadedUrl });
      } catch (error: any) {
        showToast('error', error?.message || 'Failed to upload image. Please try again.');
      }
    }
  };

  const handleSubmitPromotion = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const normalizedAdditionalFields = additionalFields
        .map((field) => ({
          label: field.label.trim(),
          value: field.value.trim()
        }))
        .filter((field) => field.label && field.value);

      const extraDetailsLines: string[] = [];
      if (registrationDeadline) {
        extraDetailsLines.push(`Registration Deadline: ${registrationDeadline}`);
      }
      normalizedAdditionalFields.forEach((field) => {
        extraDetailsLines.push(`${field.label}: ${field.value}`);
      });

      const descriptionWithExtras = extraDetailsLines.length > 0
        ? `${formData.description.trim()}\n\n--- Additional Details ---\n${extraDetailsLines.map((line) => `- ${line}`).join('\n')}`
        : formData.description.trim();

      const payload = {
        title: formData.title,
        description: descriptionWithExtras,
        category: formData.category,
        collegeName: formData.collegeName,
        image: formData.image,
        createdBy: user?.id || 'anonymous',
        status: 'pending'
      };

      // Get token from localStorage if not in context (backup)
      const authToken = token || localStorage.getItem('token');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.post(`${API_URL}/api/promotions`, payload, {
        headers
      });

      console.log('Promotion submitted successfully:', response.data);
      setShowApplyModal(false);
      setFormData({ title: '', description: '', category: '', collegeName: '', image: '' });
      setRegistrationDeadline('');
      setAdditionalFields([]);
      setImagePreview('');
      showToast('success', 'Promotion request submitted successfully. Awaiting admin review.');
      fetchPromotions();
    } catch (error: any) {
      console.error('Failed to submit promotion:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to submit promotion request';
      
      // Handle 401 Unauthorized specifically
      if (error.response?.status === 401) {
        showToast('info', 'Please log in to submit a promotion request. Redirecting to sign in...');
        navigate('/auth');
      } else {
        showToast('error', errorMessage);
      }
    }
  };

  const handleAddAdditionalField = () => {
    setAdditionalFields((prev) => [
      ...prev,
      {
        id: Date.now(),
        label: '',
        value: ''
      }
    ]);
  };

  const handleUpdateAdditionalField = (
    fieldId: number,
    key: 'label' | 'value',
    value: string
  ) => {
    setAdditionalFields((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, [key]: value }
          : field
      )
    );
  };

  const handleRemoveAdditionalField = (fieldId: number) => {
    setAdditionalFields((prev) => prev.filter((field) => field.id !== fieldId));
  };

  return (
    <div className="nexaprom-page">
      {toastState && (
        <div className={`nexaprom-toast ${toastState.type}`} role="status" aria-live="polite">
          <span>{toastState.message}</span>
          <button
            type="button"
            className="toast-close-btn"
            onClick={() => setToastState(null)}
            aria-label="Close toast"
          >
            <CloseIcon size={12} />
          </button>
        </div>
      )}

      {/* Header */}
      <motion.header
        className="nexaprom-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="nexaprom-header-content">
          <motion.button
            className="back-btn"
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            ← BACK
          </motion.button>
          <h1>NEXAPROM</h1>
          <motion.button
            className="apply-btn"
            onClick={() => setShowApplyModal(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            + Apply for Promotion
          </motion.button>
        </div>
      </motion.header>

      {/* Hero Section */}
      <motion.section
        className="nexaprom-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <h2>Explore College Events & Technologies</h2>
          <p>Discover promotions showcasing innovation, events, and excellence from all over the indian prestigious institutions.</p>
        </div>
      </motion.section>

      {/* Promotions Grid */}
      <section className="promotions-section">
        <div className="container">
          {loading ? (
            <div className="loading">Loading promotions...</div>
          ) : promotions.length === 0 ? (
            <div className="empty-state">
              <p> No promotions available yet!</p>
              <p style={{fontSize: '0.9rem', marginTop: '10px', opacity: 0.8}}>Be the first to share your event or technology showcase</p>
              <motion.button
                className="apply-btn"
                onClick={() => setShowApplyModal(true)}
                whileHover={{ scale: 1.05 }}
              >
                + Be the first to apply →
              </motion.button>
            </div>
          ) : (
            <motion.div className="promotions-grid">
              {promotions.map((promotion, index) => (
                <motion.div
                  key={promotion.id}
                  className="promotion-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  {promotion.image && (
                    <div className="card-image">
                      <img src={promotion.image} alt={promotion.title} />
                    </div>
                  )}
                  <div className="card-content">
                    <div className="card-tags">
                      {(promotion.tags && promotion.tags.length > 0) ? (
                        promotion.tags.map((tag) => (
                          <span key={tag} className="tag">{tag}</span>
                        ))
                      ) : (
                        <span className="tag">{promotion.category}</span>
                      )}
                    </div>
                    <h3>{promotion.title}</h3>
                    <p className="college-name"><strong>{promotion.collegeName}</strong></p>
                    <p>{promotion.description}</p>
                    <motion.button
                      className="learn-more-btn"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Learn More →
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Apply Modal */}
      {showApplyModal && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowApplyModal(false)}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Apply for Promotion</h2>
              <motion.button
                className="close-btn"
                onClick={() => setShowApplyModal(false)}
                whileHover={{ scale: 1.1, rotate: 90 }}
              >
                <CloseIcon size={14} />
              </motion.button>
            </div>

            <form onSubmit={handleSubmitPromotion} className="promotion-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Event/Tech name"
                  required
                />
              </div>

              <div className="form-group">
                <label>College Name *</label>
                <input
                  type="text"
                  value={formData.collegeName}
                  onChange={(e) => setFormData({ ...formData, collegeName: e.target.value })}
                  placeholder="Enter your college name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your event or technology"
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Hackathon, Workshop, Festival"
                  required
                />
              </div>

              <div className="form-group">
                <label>Registration Deadline</label>
                <input
                  type="date"
                  value={registrationDeadline}
                  onChange={(e) => setRegistrationDeadline(e.target.value)}
                />
              </div>

              <div className="form-group additional-fields-group">
                <div className="additional-fields-header">
                  <label>Additional Custom Fields</label>
                  <button
                    type="button"
                    className="add-field-btn"
                    onClick={handleAddAdditionalField}
                  >
                    + Add Custom Field
                  </button>
                </div>

                {additionalFields.length === 0 && (
                  <p className="additional-fields-hint">
                    Add optional details like Problem Statement, Eligibility, Venue, Team Size, or anything else.
                  </p>
                )}

                {additionalFields.map((field) => (
                  <div key={field.id} className="additional-field-row">
                    <input
                      type="text"
                      placeholder="Field name (e.g., Problem Statement)"
                      value={field.label}
                      onChange={(e) => handleUpdateAdditionalField(field.id, 'label', e.target.value)}
                    />
                    <textarea
                      placeholder="Field value"
                      rows={2}
                      value={field.value}
                      onChange={(e) => handleUpdateAdditionalField(field.id, 'value', e.target.value)}
                    />
                    <button
                      type="button"
                      className="remove-field-btn"
                      onClick={() => handleRemoveAdditionalField(field.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label>Image</label>
                <div className="file-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="file-input"
                    id="promo-image"
                  />
                  <label htmlFor="promo-image" className="file-label">
                    <FolderIcon size={16} /> Choose Image (Max 5MB)
                  </label>
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview('');
                          setFormData({ ...formData, image: '' });
                        }}
                        className="remove-image"
                      >
                        <CloseIcon size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <motion.button
                  type="submit"
                  className="submit-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Submit for Review
                </motion.button>
                <motion.button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowApplyModal(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Nexaprom;
